import React, { useEffect, useRef, useCallback } from "react";

const SITE_KEY = process.env.REACT_APP_TURNSTILE_SITE_KEY;

/**
 * Renders a Cloudflare Turnstile CAPTCHA widget and calls onVerify(token)
 * once the visitor completes it. Used on Login and on public LR search
 * (PublicTrack) to slow down bots/brute-force without a Google dependency.
 *
 * If REACT_APP_TURNSTILE_SITE_KEY isn't set (e.g. local dev before you've
 * created a Turnstile site), this renders nothing and calls onVerify(null)
 * immediately — the parent form still works, it just skips the check.
 * NEVER rely on this to skip checks in production: the real enforcement
 * lives server-side (Supabase Auth's captcha setting for login, and the
 * `track` Edge Function's verifyTurnstile() call for LR search).
 */
export default function Turnstile({ onVerify, action }) {
  const containerRef = useRef(null);
  const widgetIdRef = useRef(null);

  const renderWidget = useCallback(() => {
    if (!SITE_KEY) {
      onVerify(null);
      return;
    }
    if (!window.turnstile || !containerRef.current) return;
    if (widgetIdRef.current !== null) return; // already rendered

    widgetIdRef.current = window.turnstile.render(containerRef.current, {
      sitekey: SITE_KEY,
      action,
      callback: (token) => onVerify(token),
      "expired-callback": () => onVerify(null),
      "error-callback": () => onVerify(null),
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!SITE_KEY) {
      onVerify(null);
      return;
    }
    if (window.turnstile) {
      renderWidget();
      return;
    }
    // Script tag is loaded async in index.html — poll briefly until it's ready
    const interval = setInterval(() => {
      if (window.turnstile) {
        clearInterval(interval);
        renderWidget();
      }
    }, 100);
    return () => clearInterval(interval);
  }, [renderWidget, onVerify]);

  if (!SITE_KEY) return null;

  return <div ref={containerRef} className="my-2" data-testid="turnstile-widget" />;
}
